import Axios, { AxiosError, AxiosProgressEvent, AxiosResponse } from 'axios';
import createAuthRefreshInterceptor, { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';
import configLoader from '@/runtime-config-loader';
import Session from '@/model/Session';
import Photo from '@/model/Photo';
import User from '@/model/User';
import SessionListElement from '@/model/SessionListElement';
import Watermark from '@/model/Watermark';
import ClientLink from '@/model/ClientLinkListElement';
import Folder from '@/model/Folder';
import { OrderDetails } from '@/model/OrderDetails';
import { PriceList } from '@/model/PriceList';
import { PrintFormat } from '@/model/PrintFormat';
import { PriceListProduct } from '@/model/PriceListProduct';
import { BusinessEntity, BusinessEntityAddress, BusinessEntityType } from '@/model/BusinessEntity';
import { normalizeUserAccountData, UserAccountData } from '@/model/UserAccountData';
import { getModule } from 'vuex-module-decorators';
import { SessionNotFound } from '@/error/sessionNotFound';
import sessionGuard from '@/service/sessionGuard';

Axios.defaults.withCredentials = true;

let authGeneration = 0;

Axios.interceptors.request.use((config) => {
    (config as any)._authGeneration = authGeneration;
    return config;
});

interface Dimensions {
    width: number;
    height: number;
}

export interface PhotoData {
    filename: string;
    dimensions: Dimensions;
    largeThumbnailDimensions: Dimensions;
    smallThumbnailDimensions: Dimensions;
    contentType: string;
    checksum: string;
    perceptualHash: string;
}

export interface SessionAgreementSettings {
    agreeSocial: boolean;
    agreeEmail: boolean;
    agreeSms: boolean;
    agreePhysical: boolean;
    agreeWebsite: boolean;
    websiteUrl: string;
    customPlatformName: string;
    platforms: {
        facebook: boolean;
        instagram: boolean;
        tiktok: boolean;
        pinterest: boolean;
        flickr: boolean;
        snapchat: boolean;
        px500: boolean;
        oneX: boolean;
        inna: boolean;
    };
}

export interface SessionAgreementDraftResponse {
    settings: SessionAgreementSettings;
    currentVersion: null | {
        id: string;
        versionNumber: number;
        templateVersion: number;
        createdAt: string;
    };
    renderedTextSnapshot: string | null;
}

export type { BusinessEntityType, BusinessEntityAddress, BusinessEntity, UserAccountData };

// Preserve the original axios error shape when refresh fails so callers can
// handle unauthorized bootstrap as an anonymous session instead of crashing.
createAuthRefreshInterceptor(Axios, async (error) => {
    const requestGeneration = (error.config as any)?._authGeneration;
    const expiresAt = await refreshSession();

    if (!expiresAt) {
        await expireAuthenticatedSession(requestGeneration);
        throw error;
    }
}, {
    shouldRefresh: (error) => isRefreshableUnauthorized(error),
});

let refreshSessionPromise: Promise<number | null> | null = null;
let refreshSessionController: AbortController | null = null;

function registerAuthMetadata(data: any): number | null {
    const rawExpiresAt = data?.auth?.refreshTokenExpiresAt ?? data?.refresh_token_expiration;
    const expiresAt = typeof rawExpiresAt === 'number' ? rawExpiresAt : Number(rawExpiresAt);
    if (Number.isFinite(expiresAt) && expiresAt > 0) {
        sessionGuard.registerRefreshTokenExpiry(expiresAt);
        return expiresAt;
    }

    return null;
}

export function beginAuthenticatedSession() {
    authGeneration += 1;
    cancelRefreshSession();
}

function isRefreshableUnauthorized(error: AxiosError): boolean {
    return error.response?.status === 401 &&
        (error.response.data as { refreshable?: boolean })?.refreshable === true &&
        (error.config as any)?._authGeneration === authGeneration;
}

async function expireAuthenticatedSession(requestGeneration: number) {
    if (requestGeneration !== authGeneration) {
        return;
    }
    const [{ default: UserModule }, { default: store }] = await Promise.all([
        import('@/store/modules/User'),
        import('@/store'),
    ]);
    const userModule = getModule(UserModule, store);
    if (requestGeneration === authGeneration && userModule.user) {
        if (sessionGuard.expire('unauthorized')) {
            userModule.setLoggedOut(true);
        }
    }
}

export async function refreshSession(): Promise<number | null> {
    if (!refreshSessionPromise) {
        const controller = new AbortController();
        refreshSessionController = controller;
        const request = (async () => {
            const refresh = async () => {
                const config = await configLoader;
                const response = await fetch(`${config.backendUrl}/user/token/refresh`, {
                    method: 'POST',
                    credentials: 'include',
                    signal: controller.signal,
                });

                if (response.status === 401) {
                    return null;
                }
                if (response.status >= 400) {
                    throw new Error(`Session refresh failed with status ${response.status}`);
                }

                const data = await response.json().catch(() => null);
                return controller.signal.aborted ? null : registerAuthMetadata(data);
            };
            return navigator.locks?.request
                ? navigator.locks.request('raws-session-refresh', refresh)
                : refresh();
        })().finally(() => {
            if (refreshSessionPromise === request) {
                refreshSessionPromise = null;
            }
            if (refreshSessionController === controller) {
                refreshSessionController = null;
            }
        });
        refreshSessionPromise = request;
    }

    return refreshSessionPromise;
}

function cancelRefreshSession() {
    refreshSessionController?.abort();
    refreshSessionController = null;
    refreshSessionPromise = null;
}

export type OrderStatus = 'unpaid'|'paid'|'refund'|'cancelled'|'inprint'|'sent';

export interface PriceListListElement {
    id: string;
    name: string;
    formats: string[];
    sessionCount: number;
}

export interface WelcomeMessageSetDto {
    id: string;
    title: string;
    welcomeMessage: string;
    purchaseMessage: string;
}

export interface DeleteWelcomeMessageSetResult {
    deletedId: string;
    detachedSessionIds: string[];
}

function mapPriceList(data: PriceList) {
    return {
        ...data,
        formats: data.formats.map((format) => PrintFormat.fromData(format)),
        products: data.products.map((product) => PriceListProduct.fromData(product)),
    };
}

function mapUser(data: any): User {
    return {
        ...data,
        storageLimit: Number.parseInt(data.storageLimit),
        paymentReceiving: data.paymentReceiving ?? data.receivePayments ?? false,
    } as User;
}

// storage-service can't verify session ownership itself, so backend proves it owns
// the OWNS_SESSION check and hands out a short-lived token tied to this sessionId;
// storage-service only has to verify the signature. Sent as a bearer header with
// cookies suppressed so it - not the long-lived login cookie - decides identity.
async function getWatermarkToken(sessionId: string): Promise<string> {
    const config = await configLoader;
    const response = await Axios.get(`${config.backendUrl}/sessions/${sessionId}/watermark-token`);

    return response.data.token;
}

export default {
    async checkSession(): Promise<boolean> {
        const config = await configLoader;
        try {
            await Axios.get(`${config.backendUrl}/user`);
            return true;
        } catch (error) {
            return !Axios.isAxiosError(error) || error.response?.status !== 401;
        }
    },
    async createSession(name: string) {
        const config = await configLoader;
        const response = await Axios.post(`${config.backendUrl}/sessions`, { name });

        return response.data.id;
    },
    async updateSession(session: Session, regenerateWatermark = false) {
        const config = await configLoader;
        const backendSession = {
            ...session,
            regenerateWatermark,
            watermark: session.watermark ? {
                opacity: session.watermark.opacity,
                size: session.watermark.size,
                enabled: session.watermark.enabled,
                spacing: session.watermark.spacing,
                antiAiLevel: session.watermark.antiAiLevel,
            } : null,
        };
        const response = await Axios.put(
            `${config.backendUrl}/sessions/${session.id}`,
            backendSession,
        );

        return response.data.id;
    },
    async deleteSession(id: string) {
        const config = await configLoader;
        await Axios.delete(`${config.backendUrl}/sessions/${id}`);
    },
    async loadSession(id: string) {
        const config = await configLoader;
        try {
            const response = await Axios.get(`${config.backendUrl}/sessions/${id}`);

            let watermark: Watermark|null = null;

            if (response.data.watermark.initiated) {
                if (response.data.watermark.enabled) {
                    const watermarkToken = await getWatermarkToken(id);
                    const watermarkResponse = await fetch(
                        `${config.storageUrl}/watermarks/${id}`,
                        {
                            headers: { Authorization: `Bearer ${watermarkToken}` },
                        },
                    );
                    if (!watermarkResponse.ok) {
                        throw new Error(`Failed to load watermark image for session ${id}: ${watermarkResponse.status}`);
                    }
                    const watermarkBinary = await watermarkResponse.arrayBuffer();

                    watermark = {
                        enabled: true,
                        image: new Uint8Array(watermarkBinary),
                        opacity: response.data.watermark.opacity,
                        size: response.data.watermark.size,
                        spacing: response.data.watermark.spacing,
                        antiAiLevel: response.data.watermark.antiAiLevel,
                    };
                } else {
                    watermark = {
                        enabled: false,
                        opacity: response.data.watermark.opacity,
                        size: response.data.watermark.size,
                        spacing: response.data.watermark.spacing,
                        antiAiLevel: response.data.watermark.antiAiLevel,
                    };
                }
            }

            return new Session(
                id,
                response.data.name,
                watermark,
                response.data.pricelistId,
                response.data.finalizationEnabled,
                response.data.downloadEnabled,
                response.data.finalized,
                response.data.finalizationMode,
                response.data.orderCount,
                response.data.chatEnabled,
                response.data.welcomeMessageSetId || null,
            );
        } catch (e) {
            if (e.response && e.response.status === 404) {
                throw new SessionNotFound(id);
            }
            throw e;
        }
    },
    async loadSessionPhotos(id: string): Promise<Photo[]> {
        const config = await configLoader;
        const response = await Axios.get(`${config.backendUrl}/photos/${id}`);

        return response.data.map((photo: any): Photo => ({
            dataHash: photo.checksum,
            filename: photo.filename,
            dimensions: photo.dimensions,
            id: photo.id,
            largeThumbDimensions: photo.largeThumbnailDimensions,
            smallThumbDimensions: photo.smallThumbnailDimensions,
            perceptualHash: photo.perceptualHash,
            largeThumbUrl: `${config.storageUrl}/photos/${id}/${photo.id}/large-thumb`,
            smallThumbUrl: `${config.storageUrl}/photos/${id}/${photo.id}/small-thumb`,
            recommended: photo.recommended,
            finalized: photo.finalized,
            surcharge: photo.surcharge,
            saleMode: photo.saleMode,
            orderedBy: photo.orderedBy,
            likedBy: photo.likedBy,
        }));
    },
    async createUploadTicket(sessionId: string, files: Array<{ id: string; sizeInBytes: number; }>): Promise<UploadTicket & { data: Record<string, any> }> {
        const config = await configLoader;
        const response = await Axios.post(`${config.storageUrl}/upload-ticket`, {
            sessionId,
            files,
        });

        return {
            serviceName: response.data.service,
            data: response.data.data,
            sessionId,
        };
    },
    async uploadFile(
        sessionId: string,
        photoId: string,
        type: 'small-thumb'|'large-thumb'|'no-watermark'|'original',
        binaryData: Blob,
        progressCallback: (uploaded: number, overall: number) => void,
        signal?: AbortSignal,
    ) {
        const config = await configLoader;
        const formData = new FormData();
        const fileUrl = `${config.storageUrl}/photos/${sessionId}/${photoId}/${type}`;
        formData.append('image', binaryData);

        await Axios.post(
            fileUrl,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                signal,
                onUploadProgress: (e: AxiosProgressEvent) => {
                    progressCallback(e.loaded, e.total || 0);
                },
            },
        );

        return fileUrl;
    },
    async deleteFile(
        sessionId: string,
        photoId: string,
        type: 'small-thumb'|'large-thumb'|'no-watermark'|'original',
    ) {
        const config = await configLoader;
        const fileUrl = `${config.storageUrl}/photos/${sessionId}/${photoId}/${type}`;
        await Axios.delete(fileUrl);

        return fileUrl;
    },
    async uploadLogo(
        file: File,
    ) {
        const config = await configLoader;
        const formData = new FormData();
        const fileUrl = `${config.storageUrl}/logos`;
        formData.append('image', file);

        await Axios.post(
            fileUrl,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            },
        );
    },
    async loadLogo(photographerId: string) {
        const config = await configLoader;
        const img = document.createElement('img');
        img.style.visibility = 'hidden';
        const logoUrl = `${config.storageUrl}/logos/${photographerId}?${Math.floor(Math.random() * 10000)}`;
        try {
            img.src = logoUrl;
            await new Promise<void>((resolve, reject) => {
                img.addEventListener('load', () => resolve());
                img.addEventListener('error', () => reject('Image could not be loaded'));
            });
        } catch (e) {
            return null;
        }
        img.remove();

        return logoUrl;
    },
    async uploadWatermark(sessionId: string, watermark: Watermark) {
        const config = await configLoader;
        if (watermark.enabled) {
            const watermarkToken = await getWatermarkToken(sessionId);
            const formData = new FormData();
            formData.append('watermarkImage', new Blob([ watermark.image! ]));
            await Axios.post(
                `${config.storageUrl}/watermarks/${sessionId}`,
                formData,
                { withCredentials: false, headers: { Authorization: `Bearer ${watermarkToken}` } },
            );
        }
    },
    async deleteWatermark(sessionId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/watermarks/${sessionId}`,
        );
        await (async () => {
            const watermarkToken = await getWatermarkToken(sessionId);
            await Axios.delete(
                `${config.storageUrl}/watermarks/${sessionId}`,
                { withCredentials: false, headers: { Authorization: `Bearer ${watermarkToken}` } },
            );
        })().catch(() => undefined);
    },
    async savePhoto(photoId: string, sessionId: string, photoData: PhotoData) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/photos/${sessionId}/${photoId}`,
            photoData,
        );
    },
    async updatePhoto(photoId: string, sessionId: string, photoData: PhotoData) {
        const config = await configLoader;
        await Axios.put(
            `${config.backendUrl}/photos/${sessionId}/${photoId}`,
            photoData,
        );
    },
    async deletePhoto(photoId: string) {
        const config = await configLoader;
        await Axios.delete(`${config.backendUrl}/photos/${photoId}`);
    },
    async deletePhotos(photoIds: string[]) {
        const config = await configLoader;
        await Axios.delete(`${config.backendUrl}/photos`, {
            data: { photoIds },
        });
    },
    async authenticateFacebook(fbToken: string, code?: string) {
        const config = await configLoader;
        const body: { fbToken: string; testCode?: string; } = { fbToken };
        if (code) {
            body.testCode = code;
        }
        const response = await Axios.post(
            `${config.backendUrl}/user/auth/fb`,
            body,
        );
        registerAuthMetadata(response.data);

        return mapUser(response.data);
    },
    async authenticatePassword(email: string, password: string) {
        const config = await configLoader;
        const response = await Axios.post(
            `${config.backendUrl}/user/auth/email`,
            {
                email,
                password,
            },
        );
        registerAuthMetadata(response.data);

        return mapUser(response.data);
    },
    async logout() {
        authGeneration += 1;
        sessionGuard.stop();
        cancelRefreshSession();
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/user/logout`,
            undefined,
            { skipAuthRefresh: true } as AxiosAuthRefreshRequestConfig,
        );
    },
    async getUser() {
        const config = await configLoader;
        try {
            const response = await Axios.get(`${config.backendUrl}/user`);
            registerAuthMetadata(response.data);
            return mapUser(response.data);
        } catch (e) {
            if ((e as AxiosError).response?.status === 401) {
                return null;
            }
            throw e;
        }
    },
    async loadUserSessionList(userId: string): Promise<{ sessions: SessionListElement[], folders: Folder[] }> {
        const config = await configLoader;
        const response = await Axios.get(`${config.backendUrl}/sessions/list/${userId}`);

        const sessions = response.data.sessions.map(
            ({
                id, name, creationTime, thumbnail, watermark, shared, pricelistId, packetId,
                finalized, ordered, downloadEnabled, finalizationEnabled, chatEnabled, welcomeMessageSetId,
            }: {
                id: string,
                name: string,
                creationTime: number,
                thumbnail: { photoId: string },
                watermark: Watermark,
                shared: boolean,
                pricelistId: string,
                packetId: string,
                finalized: boolean,
                ordered: boolean,
                finalizationEnabled: boolean,
                downloadEnabled: boolean,
                chatEnabled: boolean,
                welcomeMessageSetId: string | null,
            }) => (
                {
                    id,
                    name,
                    creationTime: new Date(creationTime * 1000),
                    thumbnail: thumbnail ? { ...thumbnail, url: `${config.storageUrl}/photos/${id}/${thumbnail.photoId}/small-thumb` } : thumbnail,
                    watermark,
                    shared,
                    pricelistId,
                    packetId,
                    finalized,
                    ordered,
                    finalizationEnabled,
                    downloadEnabled,
                    chatEnabled,
                    welcomeMessageSetId,
                }
            )) as SessionListElement[];

        return {
            sessions,
            folders: response.data.folders || [],
        };
    },
    async generateLink(sessionId: string, group: boolean): Promise<ClientLink> {
        const config = await configLoader;
        const response = await Axios.post(
            `${config.backendUrl}/links/${sessionId}`,
            {
                group,
            },
        );

        return { registrationsCount: 0, ...response.data, url: `${config.clientFrontendUrl}/${response.data.code}`};
    },
    async getClientLinks(sessionId: string): Promise<ClientLink[]> {
        const config = await configLoader;
        const response = await Axios.get(`${config.backendUrl}/links/${sessionId}`);

        return response.data.map((link: Omit<ClientLink, 'url'>) => ({
            ...link,
            url: `${config.clientFrontendUrl}/${link.code}`,
        }));
    },
    async getSessionAgreementDraft(sessionId: string): Promise<SessionAgreementDraftResponse> {
        const config = await configLoader;
        const response = await Axios.get(`${config.backendUrl}/sessions/${sessionId}/agreements/draft`);

        return response.data;
    },
    async createSessionAgreementDraft(sessionId: string, settings: SessionAgreementSettings): Promise<SessionAgreementDraftResponse> {
        const config = await configLoader;
        const response = await Axios.post(`${config.backendUrl}/sessions/${sessionId}/agreements/draft`, settings);

        return response.data;
    },
    async updateSessionAgreementDraft(sessionId: string, settings: SessionAgreementSettings): Promise<SessionAgreementDraftResponse> {
        const config = await configLoader;
        const response = await Axios.put(`${config.backendUrl}/sessions/${sessionId}/agreements/draft`, settings);

        return response.data;
    },
    async renderAgreementPreview(sessionId: string, settings: SessionAgreementSettings): Promise<string> {
        const config = await configLoader;
        const response = await Axios.post(`${config.backendUrl}/sessions/${sessionId}/agreements/render`, settings);

        return response.data.html;
    },
    async deleteLink(linkCode: string): Promise<void> {
        const config = await configLoader;
        await Axios.delete(`${config.backendUrl}/links/${linkCode}`);
    },
    async resetLink(linkCode: string): Promise<void> {
        const config = await configLoader;
        await Axios.patch(`${config.backendUrl}/links/${linkCode}/reset`);
    },
    async disableSessionLinks(sessionId: string): Promise<void> {
        const config = await configLoader;
        await Axios.patch(`${config.backendUrl}/sessionLinks/${sessionId}/disable`);
    },
    async enableSessionLinks(sessionId: string): Promise<void> {
        const config = await configLoader;
        await Axios.patch(`${config.backendUrl}/sessionLinks/${sessionId}/enable`);
    },
    async updateStudioName(newName: string): Promise<void> {
        const config = await configLoader;
        await Axios.request({
            url: `${config.backendUrl}/user/studioName`,
            headers: {
                'Content-Type': 'application/json',
            },
            data: JSON.stringify(newName),
            method: 'PUT',
        });
    },
    async recommendMultiplePhotos(photoIds: string[]) {
        const config = await configLoader;
        await Axios.patch(
            `${config.backendUrl}/photos/recommendations`,
            photoIds.reduce(
                (p, photoId) => ({...p, [photoId]: true}),
                {} as { [id: string]: boolean },
            ),
        );
    },
    async unrecommendMultiplePhotos(photoIds: string[]) {
        const config = await configLoader;
        await Axios.patch(
            `${config.backendUrl}/photos/recommendations`,
            photoIds.reduce(
                (p, photoId) => ({...p, [photoId]: false}),
                {} as { [id: string]: boolean },
            ),
        );
    },
    async recommendPhoto(photoId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/photos/${photoId}/recommendation`,
        );
    },
    async updatePhotoSaleOptions(photoId: string, options: { surcharge: number|null; saleMode: 'all'|'noDigital'|'digitalOnly' }) {
        const config = await configLoader;
        await Axios.put(`${config.backendUrl}/photos/${photoId}/sale-options`, options);
    },
    async updatePhotosSaleOptions(photoIds: string[], options: { surcharge: number|null; saleMode: 'all'|'noDigital'|'digitalOnly' }) {
        const config = await configLoader;
        await Axios.put(`${config.backendUrl}/photos/sale-options`, { photoIds, ...options });
    },
    async unrecommendPhoto(photoId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/photos/${photoId}/recommendation`,
        );
    },
    async finalizePhoto(photoId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/photos/${photoId}/finalization`,
        );
    },
    async unfinalizePhoto(photoId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/photos/${photoId}/finalization`,
        );
    },
    async enableSessionFinalization(sessionId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/sessions/${sessionId}/finalizationEnabled`,
        );
    },
    async disableSessionFinalization(sessionId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/sessions/${sessionId}/finalizationEnabled`,
        );
    },
    async enableSessionDownload(sessionId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/sessions/${sessionId}/downloadEnabled`,
        );
    },
    async disableSessionDownload(sessionId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/sessions/${sessionId}/downloadEnabled`,
        );
    },
    async finalizeSession(sessionId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/sessions/${sessionId}/finalization`,
        );
    },
    async enableFinalizationMode(sessionId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/sessions/${sessionId}/finalizationMode`,
        );
    },
    async disableFinalizationMode(sessionId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/sessions/${sessionId}/finalizationMode`,
        );
    },
    async enableSessionChat(sessionId: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/sessions/${sessionId}/chatEnabled`,
        );
    },
    async disableSessionChat(sessionId: string) {
        const config = await configLoader;
        await Axios.delete(
            `${config.backendUrl}/sessions/${sessionId}/chatEnabled`,
        );
    },
    async getOrders(): Promise<Array<{
        orderId: string;
        sessionId: string;
        fullName: string;
        email: string;
        createdAt: {
            date: string;
            timezone_type: number;
            timezone: string;
        };
        price: string;
        chosenPhotos: number;
        chosenPrints: number;
        status: OrderStatus;
    }>> {
        const config = await configLoader;
        return (await Axios.get(
            `${config.backendUrl}/orders`,
        )).data;
    },

    async getSessionOrders(sessionId: string): Promise<Array<{
        orderId: string;
        sessionId: string;
        fullName: string;
        email: string;
        createdAt: {
            date: string;
            timezone_type: number;
            timezone: string;
        };
        price: string;
        chosenPhotos: number;
        chosenPrints: number;
        status: OrderStatus;
    }>> {
        const config = await configLoader;
        return (await Axios.get(
            `${config.backendUrl}/session/orders/${sessionId}`,
        )).data;
    },

    async getOrderDetails(orderId: string): Promise<OrderDetails> {
        const config = await configLoader;
        return (await Axios.get(
            `${config.backendUrl}/orders/${orderId}`,
        )).data;
    },

    async updateOrderStatus(orderId: string, orderStatus: OrderStatus) {
        const config = await configLoader;
        return (await Axios.patch(
            `${config.backendUrl}/orders/${orderId}/status`,
            { orderStatus },
        ));
    },

    async getUserData(): Promise<UserAccountData> {
        const config = await configLoader;
        const response = await Axios.get(
            `${config.backendUrl}/user/data`,
        );

        return normalizeUserAccountData(response.data);
    },

    async updateAccountData(accountData: UserAccountData & { twoFactorCode: string }): Promise<{ twoFactorValidUntil?: number; error?: string }> {
        const config = await configLoader;
        try {
            const response = await Axios.put(
                `${config.backendUrl}/user/data`,
                accountData,
            );
            return response.data;
        } catch (e) {
            if (e.response && e.response.status === 400) {
                return { error: e.response.data.error };
            }
            throw e;
        }
    },
    async getClients(): Promise<{
        [sessionId: string]: Array<{
            linkCode: string;
            fullName: string;
            email: string;
            claimedAt: number;
            photographerComment: string;
        }>;
    }> {
        const config = await configLoader;
        return (await Axios.get(
            `${config.backendUrl}/user/clients`,
        )).data;
    },

    async getSingleSessionClients(sessionId: string): Promise<Array<{
            linkCode: string;
            fullName: string;
            email: string;
            claimedAt: number;
            photographerComment: string;
        }>> {
        const config = await configLoader;
        return (await Axios.get(
            `${config.backendUrl}/user/clients/${sessionId}`,
        )).data;
    },

    async updateClientComment(linkCode: string, comment: string): Promise<void> {
        const config = await configLoader;
        await Axios.post(`${config.backendUrl}/links/${linkCode}/comment`, {
            comment,
        });
    },

    async changeLinkGroupSize(linkCode: string, groupSize: number) {
        const config = await configLoader;
        await Axios.put(`${config.backendUrl}/links/${linkCode}/groupSize`, {
            groupSize,
        });
    },

    async createPriceList(priceList: PriceList): Promise<PriceList> {
        const config = await configLoader;
        const response = await Axios.post<PriceList>(`${config.backendUrl}/priceLists`, priceList);

        return {
            ...response.data,
            formats: response.data.formats.map((format) => PrintFormat.fromData(format)),
            products: response.data.products.map((product) => PriceListProduct.fromData(product)),
        };
    },

    async updatePriceList(priceList: PriceList) {
        const config = await configLoader;
        const response = await Axios.put<PriceList & { photoSettingsChangedCount: number }>(`${config.backendUrl}/priceLists`, priceList);
        const { photoSettingsChangedCount, ...savedPriceList } = response.data;

        return { priceList: mapPriceList(savedPriceList), photoSettingsChangedCount };
    },

    async getPriceList(id: string) {
        const config = await configLoader;
        const response = await Axios.get<PriceList>(`${config.backendUrl}/priceLists/${id}`);

        return mapPriceList(response.data);
    },

    async getPriceLists() {
        const config = await configLoader;
        const response = await Axios.get<PriceListListElement[]>(`${config.backendUrl}/priceLists`);

        return response.data;
    },

    async deletePriceList(id: string) {
        const config = await configLoader;
        await Axios.delete<PriceListListElement[]>(`${config.backendUrl}/priceLists/${id}`);
    },

    async clonePriceList(id: string) {
        const config = await configLoader;
        await Axios.post<PriceListListElement[]>(`${config.backendUrl}/priceLists/clone/${id}`);
    },

    async selectPriceList(sessionId: string, priceListId: string|null) {
        const config = await configLoader;
        console.log({priceListId});
        await Axios.put(
            `${config.backendUrl}/sessions/${sessionId}/selectedPricelist`,
            JSON.stringify(priceListId),
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
    },

    async registerUser(email: string, password: string, testCode?: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/user/register`,
            JSON.stringify({
                email,
                password,
                testCode,
            }),
        );
    },

    async confirmEmail(code: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/user/confirm/${code}`,
        );
    },

    async changePassword(oldPassword: string, newPassword: string) {
        const config = await configLoader;
        try {
            await Axios.post(
                `${config.backendUrl}/user/changePassword`,
                {
                    oldPassword,
                    newPassword,
                },
            );
        } catch (e) {
            if (
                e.response &&
                (e.response as AxiosResponse).status === 400
            ) { return e.response.data; } else { throw e; }
        }
    },

    async requestPasswordRecovery(email: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/user/requestPasswordRecovery`,
            {
                email,
            },
        );
    },

    async resetPassword(emailCode: string, newPassword: string) {
        const config = await configLoader;
        await Axios.post(
            `${config.backendUrl}/user/recoverPassword`,
            {
                emailCode,
                newPassword,
            },
        );
    },

    async emailExists(email: string) {
        const config = await configLoader;
        const response = await Axios.get(
            `${config.backendUrl}/user/emailExists/${encodeURIComponent(email)}`,
        );
        console.log({ response });

        return response.data;
    },

    async updateLastSeenTutorialVersion(version: number) {
        const config = await configLoader;
        await Axios.put(
            `${config.backendUrl}/user/tutorialVersion`,
            {
                tutorialVersion: version,
            },
        );
    },

    async getUsedStorageBytes() {
        const config = await configLoader;
        const response = await Axios.get(
            `${config.storageUrl}/storage/usage`,
        );
        return response.data.usedStorageBytes;
    },

    async saveFolders(userId: string, folders: Folder[]): Promise<void> {
        const config = await configLoader;
        await Axios.put(`${config.backendUrl}/sessions/${userId}/folders`, folders);
    },

    async addWelcomeMessageSet(messageSet: { title: string; welcomeMessage: string; purchaseMessage: string; }) {
        const config = await configLoader;
        const response = await Axios.post(`${config.backendUrl}/user/welcomeMessageSets`, messageSet);
        return response.data as WelcomeMessageSetDto;
    },

    async updateWelcomeMessageSet(
        id: string,
        messageSet: { title: string; welcomeMessage: string; purchaseMessage: string; },
    ) {
        const config = await configLoader;
        const response = await Axios.put(`${config.backendUrl}/user/welcomeMessageSets/${id}`, messageSet);
        return response.data as WelcomeMessageSetDto;
    },

    async deleteWelcomeMessageSet(id: string) {
        const config = await configLoader;
        const response = await Axios.delete(`${config.backendUrl}/user/welcomeMessageSets/${id}`);
        return response.data as DeleteWelcomeMessageSetResult;
    },

    async listWelcomeMessageSets() {
        const config = await configLoader;
        const response = await Axios.get(`${config.backendUrl}/user/welcomeMessageSets`);
        return response.data as WelcomeMessageSetDto[];
    },

    async assignWelcomeMessageSet(sessionId: string, welcomeMessageSetId: string | null) {
        const config = await configLoader;
        await Axios.put(`${config.backendUrl}/sessions/${sessionId}/welcomeMessageSet`, {
            welcomeMessageSetId,
        });
    },

    async enableTotp2FA(): Promise<{ qrContent: string }> {
        const config = await configLoader;
        const response = await Axios.post(`${config.backendUrl}/user/enable2FA`, { type: 'totp' });

        return response.data;
    },

    async enableEmail2FA(): Promise<void> {
        const config = await configLoader;
        await Axios.post(`${config.backendUrl}/user/enable2FA`, { type: 'email' });
    },

    async confirm2FA(code: string) {
        const config = await configLoader;
        await Axios.post(`${config.backendUrl}/user/confirm2FA`, { code });
    },

    async validate2FA(code: string): Promise<{ twoFactorValidUntil: number } | {error: string; errorCode: string}> {
        const config = await configLoader;
        try {
            const response = await Axios.post(`${config.backendUrl}/user/validate2fa`, { code });
            registerAuthMetadata(response.data);
            return { twoFactorValidUntil: response.data.twoFactorValidUntil };
        } catch (e) {
            if (e.response && e.response.status === 401) {
                return e.response.data;
            }
            throw e;
        }
    },

    async sendEmail2FACode() {
        const config = await configLoader;
        return Axios.post(`${config.backendUrl}/user/sendEmail2FACode`);
    }
};
